import { useMemo } from "react";
import { Shield } from "lucide-react";
import type {
  MaturityEvaluation,
  MaturityFrameworkResponse,
  PageView,
} from "@/types/maturity";
import { DomainTable } from "../DomainTable";
import { KeyMeasuresTable } from "../KeyMeasuresTable";
import { ScoreCards } from "../ScoreCards";
import { CTABar } from "../CTABar";
import { RadarChart } from "../RadarChart";
import { ResumeBanner } from "../ResumeBanner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetQuestionnaireItems } from "@/hooks/api/query/useGetQuestionnaireItems";
import { useGetScoringAlgorithm } from "@/hooks/api/query/useGetScoringAlgorithm";
import { computePreliminaryOverallScore } from "@/utils/maturity-scoring";

type SummaryViewProps = {
  evaluation: MaturityEvaluation;
  framework: MaturityFrameworkResponse;
  onNavigate: (view: PageView) => void;
  onResetFullEval: () => void;
  isResetting?: boolean;
};

function SummaryView({
  evaluation,
  framework,
  onNavigate,
  onResetFullEval,
  isResetting,
}: SummaryViewProps) {
  const checkedCount = useMemo(() => {
    return Object.values(evaluation.preliminary_responses).filter(Boolean)
      .length;
  }, [evaluation.preliminary_responses]);

  const { data: questionnaire } = useGetQuestionnaireItems(
    evaluation.assurance_level
  );

  const liveScore = useMemo(() => {
    if (!questionnaire?.items?.length) return undefined;
    return computePreliminaryOverallScore(
      evaluation.preliminary_responses,
      questionnaire.items
    );
  }, [evaluation.preliminary_responses, questionnaire?.items]);

  const { data: scoringAlgorithm } = useGetScoringAlgorithm();

  const keyMeasureScore = useMemo<number | null>(() => {
    if (evaluation.eval_state !== "FULL_COMPLETE") return null;
    if (!scoringAlgorithm) return null;
    const levelConfig = scoringAlgorithm.assurance_levels.find(
      (l) =>
        l.name === evaluation.assurance_level ||
        l.code === evaluation.assurance_level
    );
    const codes = levelConfig?.key_measures ?? [];
    const scores: number[] = [];
    for (const code of codes) {
      const d = evaluation.full_responses[`${code}-D`];
      const i = evaluation.full_responses[`${code}-I`];
      if (d === undefined || i === undefined) continue;
      scores.push((Number(d) + Number(i)) / 2);
    }
    if (scores.length === 0) return null;
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  }, [
    evaluation.eval_state,
    evaluation.assurance_level,
    evaluation.full_responses,
    scoringAlgorithm,
  ]);

  const showResumeBanner =
    evaluation.eval_state === "NOT_STARTED" && checkedCount > 0;

  const scores =
    evaluation.eval_state === "FULL_COMPLETE"
      ? evaluation.full_scores
      : evaluation.preliminary_scores;

  return (
    <div className="max-w-[1120px] mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Shield className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h1
            data-testid="maturity-page-title"
            className="text-2xl font-heading font-bold text-slate-800"
          >
            Evaluarea Maturității
          </h1>
          <p className="text-sm text-slate-500">
            CyFun® 2025 — Cadrul Național de Securitate Cibernetică
          </p>
        </div>
      </div>

      {showResumeBanner && (
        <ResumeBanner checkedCount={checkedCount} onNavigate={onNavigate} />
      )}

      {evaluation.eval_state === "FULL_COMPLETE" ? (
        <Tabs defaultValue="domains" className="space-y-3">
          <TabsList>
            <TabsTrigger value="domains">Pe domenii</TabsTrigger>
            <TabsTrigger value="keyMeasures">Pe măsuri-cheie</TabsTrigger>
          </TabsList>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4 items-start">
            <div className="min-w-0">
              <TabsContent value="domains" className="mt-0">
                <DomainTable
                  evaluation={evaluation}
                  categories={framework.categories}
                />
              </TabsContent>
              <TabsContent value="keyMeasures" className="mt-0">
                <KeyMeasuresTable
                  evaluation={evaluation}
                  requirements={framework.requirements}
                />
              </TabsContent>
            </div>
            <ScoreCards
              evaluation={evaluation}
              totalChecked={checkedCount}
              liveScore={liveScore}
              keyMeasureScore={keyMeasureScore}
            />
          </div>
        </Tabs>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4 items-start">
          <DomainTable
            evaluation={evaluation}
            categories={framework.categories}
          />
          <ScoreCards
            evaluation={evaluation}
            totalChecked={checkedCount}
            liveScore={liveScore}
            keyMeasureScore={keyMeasureScore}
          />
        </div>
      )}

      <CTABar
        evalState={evaluation.eval_state}
        sessionId={evaluation.form_session_id}
        assuranceLevel={evaluation.assurance_level}
        fullResponses={evaluation.full_responses}
        fullNotes={evaluation.full_notes}
        onNavigate={onNavigate}
        onResetFullEval={onResetFullEval}
        isResetting={isResetting}
      />

      <RadarChart
        categories={framework.categories}
        scores={scores}
        targetScore={evaluation.target_score}
      />
    </div>
  );
}

export default SummaryView;
